import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import swal from "sweetalert";

const formatCurrency = (val, code = "USD") => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatAddress = (addr) => {
  if (!addr) return "—";
  if (typeof addr === "string") return addr;
  const parts = [
    addr.Addr1 || addr.Line1,
    addr.Addr2 || addr.Line2,
    addr.Addr3 || addr.Line3,
    addr.City || addr.city,
    addr.State || addr.CountrySubDivisionCode || addr.state,
    addr.PostalCode || addr.postalCode,
    addr.Country || addr.country,
  ].filter(Boolean);
  return parts.join(", ") || "—";
};

const statusColor = (status = "") => {
  const map = {
    Accepted: {
      bg: "bg-[#F0FEF4]",
      text: "text-[#008236]",
      border: "border-[#0a7822a6]",
      dot: "bg-[#0a7822a6]",
    },
    Open: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      dot: "bg-amber-500",
    },
    Pending: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      dot: "bg-amber-500",
    },
    Closed: {
      bg: "bg-gray-100",
      text: "text-gray-500",
      border: "border-gray-200",
      dot: "bg-gray-400",
    },
    Rejected: {
      bg: "bg-red-50",
      text: "text-red-600",
      border: "border-red-200",
      dot: "bg-red-500",
    },
  };
  return (
    map[status] || {
      bg: "bg-gray-100",
      text: "text-gray-500",
      border: "border-gray-200",
      dot: "bg-gray-400",
    }
  );
};

const StatusBadge = ({ status }) => {
  const c = statusColor(status || "");
  const label = status ? status : "—";
  return (
    <span
      className={`inline-flex capitalize items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
    >
      <span className={`size-1.5 rounded-full ${c.dot}`} />
      {label}
    </span>
  );
};

const normalizeQBDEstimate = (est) => {
  if (!est) return null;
  const raw = est.raw || {};

  const customerName =
    est.customerName ||
    est.customer_name ||
    raw.CustomerRef?.FullName ||
    raw.CustomerRef?.name ||
    "Customer";

  const email = est.email || raw.BillEmail?.Address || est.displayEmail || "—";

  const docNumber =
    est.docNumber || est.doc_number || est.refNumber || raw.RefNumber || raw.DocNumber || "—";

  let status = "Open";
  if (est.status) {
    const s = String(est.status).toLowerCase();
    if (s === "pending" || s === "open") status = "Open";
    else if (s === "closed") status = "Closed";
    else if (s === "accepted") status = "Accepted";
    else if (s === "rejected") status = "Rejected";
    else status = est.status.charAt(0).toUpperCase() + est.status.slice(1);
  } else if (raw.IsFullyInvoiced || raw.IsActive === "false" || raw.IsActive === false) {
    status = "Closed";
  }

  const rawLines =
    est.originalLineItems?.length
      ? est.originalLineItems
      : est.markupLineItems?.length
      ? est.markupLineItems
      : est.lines?.length
      ? est.lines
      : est.EstimateLineRet?.length
      ? est.EstimateLineRet
      : est.Line?.length
      ? est.Line
      : raw.EstimateLineRet?.length
      ? raw.EstimateLineRet
      : raw.Line?.length
      ? raw.Line
      : [];

  const lines = rawLines.map((l, idx) => ({
    id: l.itemListId || l.ItemListID || l.ItemRef?.ListID || l.Id || l._id || idx,
    name:
      l.name ||
      l.itemName ||
      l.ItemRef?.FullName ||
      l.ItemRef?.name ||
      l.raw?.ItemRef?.FullName ||
      l.description ||
      l.Desc ||
      l.Description ||
      "—",
    qty: Number(l.quantity || l.Quantity || l.Qty || 1),
    unitPrice: Number(l.rate || l.Rate || l.UnitPrice || 0),
    amount: Number(
      l.amount ||
        l.Amount ||
        Number(l.quantity || l.Quantity || l.Qty || 1) * Number(l.rate || l.Rate || l.UnitPrice || 0)
    ),
  }));

  const subTotal =
    lines.reduce((acc, line) => acc + line.amount, 0) ||
    Number(est.subtotal || est.customer_subtotal || raw.Subtotal || 0);

  const totalTax = Number(est.totalTax || est.total_tax || raw.SalesTaxTotal || 0);
  const total = Number(est.totalAmount || est.total_amount || raw.TotalAmount || subTotal + totalTax);

  const billAddr = raw.BillAddress || raw.BillAddr || est.billingAddress || est.BillAddr;
  const shipAddr = raw.ShipAddress || raw.ShipAddr || est.shippingAddress || est.ShipAddr;
  const salesTaxRate = raw.SalesTaxPercentage || est.taxDetails?.tax_percent || null;

  return {
    _id: est._id,
    qbId: est.qbId || raw.TxnID || null,
    customerListId: raw.CustomerRef?.ListID || est.CustomerRef?.ListID || est.customerListId || "",
    docNumber,
    customerName,
    email,
    status,
    txnDate: est.txnDate || est.txn_date || raw.TxnDate || est.createdAt,
    expirationDate: est.dueDate || est.DueDate || raw.DueDate || est.expirationDate || raw.ExpirationDate || null,
    createdAt: est.createdAt || raw.TimeCreated || null,
    updatedAt: est.updatedAt || raw.TimeModified || null,
    subTotal,
    totalTax,
    salesTaxRate,
    totalAmount: total,
    grandTotal: total,
    markup_type: est.markup?.type || est.markup_type || null,
    markup_amount: est.markup?.amount || est.markup_amount || 0,
    markup_value: est.markup?.value || est.markup_value || 0,
    discountAmount: Number(est.discountAmount || est.discount_amount || 0),
    BillAddr: billAddr,
    ShipAddr: shipAddr,
    Line: lines,
    taxLines: totalTax > 0 ? [{ percent: salesTaxRate || 0, amount: totalTax, netAmount: subTotal }] : [],
    currencyCode: raw.CurrencyRef?.FullName || "USD",
  };
};

export default function QBDEstimateDetail() {
  const { id } = useParams();
  const location = useLocation();
  const history = useNavigate();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const fetchEstimate = useCallback(
    async (estimateId) => {
      setPageLoading(true);
      loader(true);
      try {
        const res = await ApiClient.get(`qb-desktop/list/estimates?companyId=${companyId}`);
        if (res?.success) {
          const list = (Array.isArray(res.data) ? res.data : []).map(normalizeQBDEstimate).filter(Boolean);
          const match = list.find(
            (e) =>
              e._id === estimateId ||
              e.qbId?.toString() === estimateId ||
              e.docNumber?.toString() === estimateId
          );

          if (match) {
            setSelectedEstimate(match);
          } else {
            swal("Error", "Estimate not found.", "error").then(() =>
              history("/qb-desktop/estimates")
            );
          }
        } else {
          setSelectedEstimate(null);
        }
      } catch (err) {
        console.error("fetchEstimate error:", err);
      } finally {
        setPageLoading(false);
        loader(false);
      }
    },
    [companyId, history]
  );

  useEffect(() => {
    if (id) fetchEstimate(id);
  }, [id, fetchEstimate]);

  const handleConvertToInvoice = (estimate) => {
    history("/qb-desktop/invoices/add", {
      state: {
        estimate: {
          customerListId: estimate.customerListId,
          txnId: estimate.qbId || estimate._id,
          refNumber: estimate.docNumber !== "—" ? estimate.docNumber : "",
          lines: estimate.Line.map((l) => ({
            itemListId: l.id,
            description: l.name,
            quantity: l.qty,
            rate: l.unitPrice,
          })),
        },
      },
    });
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => history("/qb-desktop/estimates")}
            className="size-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h2 className="text-gray-900 text-xl font-semibold capitalize">
              Estimate Details
            </h2>
            <p className="text-gray-500 text-sm">
              View full details of this estimate
            </p>
          </div>
        </div>

        <div className="w-full border rounded-2xl bg-white flex flex-col">
          {pageLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="size-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !selectedEstimate ? (
            <div className="flex items-center justify-center py-20">
              <p className="text-gray-500 text-sm capitalize">
                Estimate not found.
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[#115B9D]"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                    </svg>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={selectedEstimate.status} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-5">
                {/* Customer Card */}
                <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                  <div className="size-12 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-[#115B9D]">
                      {getInitials(selectedEstimate.customerName)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold capitalize text-gray-900">
                      {selectedEstimate.customerName}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {selectedEstimate.email}
                    </p>
                  </div>
                </div>

                {/* Estimate Details */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                    Estimate Details
                  </p>
                  <div className="pt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      {
                        label: "DOC NUMBER",
                        value: `#${selectedEstimate.docNumber}`,
                      },
                      {
                        label: "TXN DATE",
                        value: formatDate(selectedEstimate.txnDate),
                      },
                      {
                        label: "EXPIRY DATE",
                        value: formatDate(selectedEstimate.expirationDate),
                      },
                      {
                        label: "CREATED ON",
                        value: formatDate(selectedEstimate.createdAt),
                      },
                      {
                        label: "LAST UPDATED",
                        value: formatDate(selectedEstimate.updatedAt),
                      },
                    ].map((d) => (
                      <div
                        key={d.label}
                        className="p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                      >
                        <p className="text-[11px] text-gray-600 font-medium capitalize tracking-wide">
                          {d.label}
                        </p>
                        <p className="text-sm text-gray-800 mt-0.5 break-words">
                          {d.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Addresses */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                    Addresses
                  </p>
                  <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      {
                        label: "BILLING ADDRESS",
                        value: formatAddress(selectedEstimate.BillAddr),
                      },
                      {
                        label: "SHIPPING ADDRESS",
                        value: formatAddress(selectedEstimate.ShipAddr),
                      },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                      >
                        <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-[#115B9D]"
                          >
                            <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] text-gray-600 font-medium capitalize tracking-wide">
                            {label}
                          </p>
                          <p className="text-sm text-gray-800 mt-0.5 break-words">
                            {value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Line Items */}
                {selectedEstimate.Line?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                      Line Items
                    </p>
                    <div className="border border-gray-100 rounded-xl overflow-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            {["Item", "Qty", "Unit Price", "Amount"].map(
                              (h, i) => (
                                <th
                                  key={h}
                                  className={`py-2.5 px-4 text-xs font-medium text-gray-500 whitespace-nowrap capitalize ${
                                    i === 0
                                      ? "text-left"
                                      : i === 1
                                      ? "text-center"
                                      : "text-right"
                                  }`}
                                >
                                  {h}
                                </th>
                              )
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {selectedEstimate.Line.map((line, i) => (
                            <tr
                              key={line.id || i}
                              className="hover:bg-gray-50/50"
                            >
                              <td className="py-2.5 px-4">
                                <p className="text-sm text-gray-700 min-w-[150px]">
                                  {line.name}
                                </p>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="text-sm text-gray-600">
                                  {line.qty}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right">
                                <span className="text-sm text-gray-600">
                                  {formatCurrency(
                                    line.unitPrice,
                                    selectedEstimate.currencyCode
                                  )}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-right">
                                <span className="text-sm font-medium text-gray-800">
                                  {formatCurrency(
                                    line.amount,
                                    selectedEstimate.currencyCode
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tax Detail */}
                {selectedEstimate.totalTax > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                      Tax Detail
                    </p>
                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                      {selectedEstimate.taxLines?.length > 0 ? (
                        selectedEstimate.taxLines.map((tl, i) => (
                          <div
                            key={i}
                            className="flex gap-x-3 items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0"
                          >
                            <p className="text-sm text-gray-500 capitalize">
                              Tax ({tl.percent}%) on{" "}
                              {formatCurrency(
                                tl.netAmount,
                                selectedEstimate.currencyCode
                              )}
                            </p>
                            <p className="text-sm font-semibold text-gray-800 capitalize">
                              {formatCurrency(
                                tl.amount,
                                selectedEstimate.currencyCode
                              )}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="flex gap-x-3 items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
                          <p className="text-sm text-gray-500 capitalize">
                            Total Tax
                          </p>
                          <p className="text-sm font-semibold text-gray-800 capitalize">
                            {formatCurrency(
                              selectedEstimate.totalTax,
                              selectedEstimate.currencyCode
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-600 capitalize tracking-widest">
                    Summary
                  </p>
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    {[
                      {
                        label: "SUBTOTAL",
                        value: formatCurrency(
                          selectedEstimate.subTotal,
                          selectedEstimate.currencyCode
                        ),
                        highlight: false,
                      },
                      {
                        label:
                          selectedEstimate.markup_type === "percentage"
                            ? `Markup (${selectedEstimate.markup_value}%)`
                            : "Markup",
                        value: formatCurrency(
                          selectedEstimate.markup_amount,
                          selectedEstimate.currencyCode
                        ),
                        highlight: false,
                        hide: !selectedEstimate.markup_amount,
                      },
                      {
                        label: "TOTAL TAX",
                        value: formatCurrency(
                          selectedEstimate.totalTax,
                          selectedEstimate.currencyCode
                        ),
                        highlight: false,
                      },
                      {
                        label: "DISCOUNT",
                        value: `- ${formatCurrency(
                          selectedEstimate.discountAmount,
                          selectedEstimate.currencyCode
                        )}`,
                        highlight: false,
                        hide: !selectedEstimate.discountAmount,
                      },
                      {
                        label: "TOTAL",
                        value: formatCurrency(
                          selectedEstimate.subTotal +
                            selectedEstimate.markup_amount -
                            selectedEstimate.discountAmount +
                            selectedEstimate.totalTax,
                          selectedEstimate.currencyCode
                        ),
                        highlight: false,
                      },
                    ]
                      .filter((r) => !r.hide)
                      .map((row) => (
                        <div
                          key={row.label}
                          className={`flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 ${
                            row.highlight ? "bg-[#115B9D]/5" : ""
                          }`}
                        >
                          <p
                            className={`text-sm font-medium ${
                              row.highlight ? "text-[#115B9D]" : "text-gray-500"
                            } capitalize`}
                          >
                            {row.label}
                          </p>
                          <p
                            className={`text-sm font-semibold ${
                              row.highlight ? "text-[#115B9D]" : "text-gray-800"
                            } capitalize`}
                          >
                            {row.value}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                {selectedEstimate.status !== "Closed" && (
                  <button
                    type="button"
                    onClick={() => handleConvertToInvoice(selectedEstimate)}
                    className="inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl font-medium px-5 py-2.5 capitalize"
                  >
                    Convert To Invoice
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => history("/qb-desktop/estimates")}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl border border-gray-200 bg-white font-medium px-5 py-2.5 capitalize"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
